"use client";

import React from "react";
import { ArrowUpRight, ShieldCheck, FileText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "@/components/common/Link";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-border bg-card rounded-lg shadow-md animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="size-12 bg-primary/20 flex items-center justify-center rounded-lg mb-6">
          <ShieldCheck className="text-primary size-6" />
        </div>
        <h3 className="font-serif text-3xl text-foreground mb-4">Inquiry Received</h3>
        <p className="font-default text-base text-muted-foreground mb-8 leading-relaxed">
          {successMessage || "Your professional inquiry has been successfully routed to our administrative team. We review all referrals within three business days."}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-secondary text-secondary hover:bg-secondary/10">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <div className="bg-card border border-border p-8 md:p-12 shadow-lg rounded-lg relative overflow-hidden">
      {}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#8FA27C]" />
      
      <Form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="name" rules={{
          required: "Full name is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-secondary">Full Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter your name" className="rounded-lg border-0 border-b border-border/40 bg-background/50 px-4 h-12 focus-visible:ring-primary/20 focus-visible:border-primary transition-all" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email address is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Invalid email format"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-secondary">Email Address</FormLabel>
                <FormControl>
                  <Input {...field} type="email" placeholder="email@agency.org" className="rounded-lg border-0 border-b border-border/40 bg-background/50 px-4 h-12 focus-visible:ring-primary/20 focus-visible:border-primary transition-all" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-secondary">Phone Number</FormLabel>
                <FormControl>
                  <Input {...field} type="tel" placeholder="(555) 000-0000" className="rounded-lg border-0 border-b border-border/40 bg-background/50 px-4 h-12 focus-visible:ring-primary/20 focus-visible:border-primary transition-all" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="service_type" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-secondary">Service Requested</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="rounded-lg border-0 border-b border-border/40 bg-background/50 px-4 h-12 focus-visible:ring-primary/20 focus-visible:border-primary w-full transition-all">
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="bg-popover border-border rounded-lg">
                    <SelectItem value="supervision">Supervised Parenting Time</SelectItem>
                    <SelectItem value="exchange">Monitored Exchange</SelectItem>
                    <SelectItem value="education">Parenting Education</SelectItem>
                    <SelectItem value="therapeutic">Therapeutic Supervision</SelectItem>
                    <SelectItem value="reporting">Observation Reporting</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-secondary">Case Context / Details</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder="Please provide relevant context or specific judicial requirements..." className="rounded-lg border-0 border-b border-border/40 bg-background/50 px-4 py-3 min-h-[120px] focus-visible:ring-primary/20 focus-visible:border-primary resize-none transition-all" />
              </FormControl>
              <FormDescription className="text-[11px] italic">
                Do not include sensitive minor identifiers in this initial inquiry.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="agreed" rules={{
        required: "Agreement is required to proceed"
      }} render={({
        field
      }) => <FormItem className="space-y-0">
              <div className="flex items-start gap-3">
                <FormControl>
                  <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-1 rounded-sm border-secondary data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                </FormControl>
                <FormLabel className="font-default text-sm text-foreground/70 leading-relaxed cursor-pointer select-none">
                  I confirm that I am a legal or social work professional and understand that Tiny to Teen Supervised Parenting & Education Services operates as a neutral, child-centered agency.
                </FormLabel>
              </div>
              <FormMessage className="mt-2" />
            </FormItem>} />

        {submitError && <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg">
            <p className="text-destructive text-sm font-default">{submitError}</p>
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-lg rounded-lg shadow-md transition-all group">
            {isSubmitting ? "Processing..." : "Submit Professional Inquiry"}
            <ArrowUpRight className="ml-2 size-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Button>
          <div className="flex items-center justify-center gap-4 mt-6 text-[11px] text-muted-foreground font-mono uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><ShieldCheck className="size-3" /> Secure</span>
            <span className="flex items-center gap-1.5"><Clock className="size-3" /> 72hr Response</span>
          </div>
        </div>
      </Form>
    </div>;
}
export default function ProfessionalInquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const standards = [{
    icon: ShieldCheck,
    label: "Neutrality",
    desc: "Strict adherence to court-ordered frameworks."
  }, {
    icon: FileText,
    label: "Documentation",
    desc: "Detailed, objective observation reporting."
  }, {
    icon: Clock,
    label: "Reliability",
    desc: "Consistent scheduling and professional punctuality."
  }];
  return <section data-section-id="2213" id="professional-inquiry" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 overflow-hidden">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24 items-start">
          {}
          <div className="lg:col-span-5">
            <div className="inline-block mb-6">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary font-semibold">
                Intake & Referrals
              </p>
              <div className="h-px w-12 bg-primary mt-2" />
            </div>
            
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] mb-8">
              A structured path for <span className="italic text-accent">professional</span> alignment.
            </h2>

            <div className="space-y-6 text-muted-foreground font-default text-base md:text-lg leading-relaxed max-w-xl">
              <p>
                Tiny to Teen Supervised Parenting & Education Services provides a resolute framework for court-involved families. We serve as a neutral bridge, ensuring that judicial requirements are met with clinical precision and child safety as the absolute priority.
              </p>
              <p>
                This inquiry portal is dedicated to attorneys, social workers, and judicial officers. For general questions, please visit our <Link to="/ResourcesPage" className="text-secondary font-medium underline decoration-secondary/30 hover:decoration-secondary transition-colors">Resources</Link> or <Link to="/FrequentlyAskedQuestionsPage" className="text-secondary font-medium underline decoration-secondary/30 hover:decoration-secondary transition-colors">FAQ</Link> pages.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-8 pt-12 border-t border-border/40">
              {standards.map((item, i) => <div key={i} data-index={i} className="space-y-3">
                  <item.icon className="size-5 text-primary" />
                  <h4 className="font-mono text-[10px] uppercase tracking-widest text-foreground font-bold">{item.label}</h4>
                  <p className="text-xs leading-relaxed text-muted-foreground">{item.desc}</p>
                </div>)}
            </div>

            <div className="mt-12 p-6 bg-card/50 border border-border/40 rounded-lg">
              <p className="font-serif italic text-lg text-foreground/80 leading-relaxed">
                "Our mission is to provide a safe, neutral, and child-centered environment for families in transition, maintaining the highest professional standards in Arizona."
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <FormProvider formKey="professional_inquiry_form_v2" formId="professional-inquiry-form" sectionName="professional-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            service_type: "",
            message: "",
            agreed: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            service_type: {
              required: true
            },
            message: {
              maxLength: 1500
            }
          }} submitText="Submit Inquiry" successMessage="Your inquiry has been received. Our administrative team will review the details and contact you within three business days." errorMessage="We encountered an error processing your request. Please verify your details and try again.">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}